/**
 * IntakeDetailsForm — the editable intake-tracker fields (the
 * capture-at-call form is primary; this is the drawer's edit surface),
 * extracted from WorkItemDrawer (UX-11 PR-2). The local IntakeSelect
 * helper (config-list-backed select-or-free-text, Amanda r1) moves with
 * it — this file is its only consumer.
 *
 * DESIGN PASS 2026-07-25 (soak r2). Three changes:
 *
 *   1. COLLAPSED BY DEFAULT. This is thirteen edit controls sitting in
 *      the middle of the read path, and it was the single biggest
 *      contributor to the drawer's length. Every fact it holds already
 *      reads, in words, in the summary above it; this is where you go to
 *      CHANGE one. It now opens on a labelled disclosure and remembers
 *      the operator's choice per browser, so someone who works with it
 *      open all day toggles once.
 *
 *   2. ONE GRID. The old 2-up grid stacked each label ABOVE its control,
 *      which put every field on a different left edge from the summary
 *      rows six pixels above it (and cramped two controls into a 440px
 *      column). Fields are now one column of label:value rows on THE
 *      drawer grid (drawerLayout LABEL_COL) — the same two left edges
 *      the read rows use.
 *
 *   3. THE TINT ZONE. Facts captured at the call are standing setup, not
 *      the work — DL4's sage-50 tint zone, the same treatment the
 *      Pre-billing "Billing settings" block wears. That is what lets the
 *      eye skip it on the way to the checklist.
 *
 * WHITE-SCREEN GUARD: `lists.referrer_type.map(…)` was unguarded, so a
 * partial options payload (a missing list) crashed the whole drawer
 * subtree to a white screen. The real fix is per-key normalization in
 * useIntakeOptions (see normalizeIntakeLists); the `?? []` here is the
 * belt to that braces — this component must not be able to crash a page
 * because one config list went missing.
 */

import { type ReactNode } from 'react';
import type { UpdateWorkItemInput, WorkItemDetail } from '@/lib/api';
import type { useIntakeOptions } from '@/lib/workspace';
import { OtherableSelect } from '@/components/workspace/intakeFields';
import { TextInput } from '@/components/primitives/TextInput';
import { ReferralSourceField } from '@/components/workspace/ReferralSourceField';
import { DrawerSection, LABEL_COL } from './drawerLayout';
import { todayStr } from '@/components/workspace/workspaceFormat';
import { WORKSPACE_DRAWER_INTAKE_DETAILS_KEY } from '@/lib/keptAcrossSignOut';
import { SelectField } from '@/components/primitives/SelectField';
import { DateInput } from '@/components/primitives/DateInput';

const CONTROL =
  'w-full rounded-base border border-sage-200 bg-paper px-2 py-1 text-[13px] text-ink ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';

/** One edit field on THE grid: label left, control right. */
function FormRow({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <label className={`${LABEL_COL} py-1 text-[13px]`}>
      <span className="pt-1.5 text-sage-400">{label}</span>
      <span className="min-w-0">{children}</span>
    </label>
  );
}

/** A config-list-backed editable field — a <select> when the list has options,
 *  else a free-text input (off-list values accepted, Amanda r1). */
function IntakeSelect({ label, value, options, onChange }: {
  label: string; value: string | null; options: string[]; onChange: (v: string | null) => void;
}): ReactNode {
  return (
    <FormRow label={label}>
      {options.length > 0 ? (
        <SelectField size="compact" value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} aria-label={label}
          className="w-full">
          <option value="">Select</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </SelectField>
      ) : (
        <input defaultValue={value ?? ''} onBlur={(e) => e.target.value !== (value ?? '') && onChange(e.target.value || null)} aria-label={label}
          className={CONTROL} />
      )}
    </FormRow>
  );
}

export interface IntakeDetailsFormProps {
  detail: WorkItemDetail;
  lists: ReturnType<typeof useIntakeOptions>['lists'];
  defaults: ReturnType<typeof useIntakeOptions>['defaults'];
  freeText: ReturnType<typeof useIntakeOptions>['freeText'];
  onEdit: (edits: UpdateWorkItemInput) => void;
}

export function IntakeDetailsForm({
  detail,
  lists,
  defaults,
  freeText,
  onEdit,
}: IntakeDetailsFormProps): ReactNode {
  return (
    <DrawerSection
      title="Intake details"
      tone="tint"
      collapsible
      defaultOpen={false}
      storageKey={WORKSPACE_DRAWER_INTAKE_DETAILS_KEY}
      testId="intake-edit"
    >
      <div data-testid="intake-edit-body">
        <FormRow label="Caller">
          <TextInput nameCase="person" defaultValue={detail.contact_name ?? ''} aria-label="Caller name" onBlur={(e) => e.target.value !== (detail.contact_name ?? '') && onEdit({ contact_name: e.target.value || null })} className={CONTROL} />
        </FormRow>
        <FormRow label="Phone">
          <input defaultValue={detail.contact_phone ?? ''} aria-label="Phone" onBlur={(e) => e.target.value !== (detail.contact_phone ?? '') && onEdit({ contact_phone: e.target.value || null })} className={CONTROL} />
        </FormRow>
        <FormRow label="Email">
          <input defaultValue={detail.contact_email ?? ''} aria-label="Email" onBlur={(e) => e.target.value !== (detail.contact_email ?? '') && onEdit({ contact_email: e.target.value || null })} className={CONTROL} />
        </FormRow>
        <FormRow label="ZIP code">
          {/* The prospective client's ZIP; five digits, checked on save. */}
          <input defaultValue={detail.prospect_zip ?? ''} aria-label="ZIP code" inputMode="numeric" onBlur={(e) => e.target.value !== (detail.prospect_zip ?? '') && onEdit({ prospect_zip: e.target.value || null })} className={CONTROL} />
        </FormRow>
        <FormRow label="Contact date">
          {/* The call already happened: never a day ahead. */}
          <DateInput defaultValue={detail.contact_date ?? ''} max={todayStr()} aria-label="Contact date"
            onChange={(e) => { const v = e.target.value; if (v && v > todayStr()) return; onEdit({ contact_date: v || null }); }} size="compact" className="w-full" />
        </FormRow>
        {/* Founder ruling 2026-07-25: "Calling for" was semantically wrong for
            contact_relationship and invited INVERTED entry (recording the
            client's relationship to the caller instead of the caller's to the
            client). The label now names the direction. Sits directly under
            Caller / Phone / Email, so whose relationship it is reads without
            having to say "caller's" a second time. */}
        <IntakeSelect label="Relationship to client" value={detail.contact_relationship} options={lists.contact_relationship} onChange={(v) => onEdit({ contact_relationship: v })} />
        <IntakeSelect label="Service" value={detail.service_requested} options={lists.service_requested} onChange={(v) => onEdit({ service_requested: v })} />
        <IntakeSelect label="Contact method" value={detail.contact_method} options={lists.contact_method} onChange={(v) => onEdit({ contact_method: v })} />
        <IntakeSelect label="Paperwork" value={detail.paperwork_method} options={lists.paperwork_method} onChange={(v) => onEdit({ paperwork_method: v })} />
        {/* referrer_type: no blank option; falls back to the config default when null (read-time default). */}
        <FormRow label="Referrer type">
          <SelectField size="compact" value={detail.referrer_type ?? defaults.referrer_type ?? ''} aria-label="Referrer type" data-testid="drawer-referrer-type-select"
            onChange={(e) => onEdit({ referrer_type: e.target.value || null })}
            className="w-full">
            {(lists.referrer_type ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
          </SelectField>
        </FormRow>
        {/* referral_text = the specific source; pairs with the referrer_type
            category above. Search-and-link a Directory org/individual when one
            exists; a brand-new referrer stays as free text. The read display up
            top keeps the Directory link when linked. */}
        <ReferralSourceField
          layout="row"
          referralText={detail.referral_text}
          referralOrgId={detail.referral_org_id}
          referralIndividualId={detail.referral_individual_id}
          linkedName={detail.referral_org_resolved_name ?? detail.referral_individual_name ?? null}
          onChange={onEdit}
        />
        {/* intake_call_by: config-name select + "Other" → free-text. Keyed by id so the toggle resets per item. */}
        <OtherableSelect key={detail.id} label="Intake call by" value={detail.intake_call_by}
          layout="row"
          options={lists.intake_call_by} freeTextLabel={freeText.intake_call_by}
          onChange={(v) => onEdit({ intake_call_by: v })} testId="drawer-intake-call-by" />
        {/* The geographic unit is NOT here: it is set in the checklist's Ready
            for Assignment section, where the unit lead assigns the case
            (WorkItemDrawer's readyForAssignmentIntro). */}
        {/* The one checkbox sits in the VALUE column, so it starts on the
            same left edge as every control above it. */}
        <div className={`${LABEL_COL} py-1.5 text-[13px]`}>
          <span />
          <label className="flex items-center gap-2 text-ink">
            <input type="checkbox" className="accent-sage-800" checked={detail.contact_is_poa} onChange={(e) => onEdit({ contact_is_poa: e.target.checked })} /> Contact is POA
          </label>
        </div>
      </div>
    </DrawerSection>
  );
}
